Returns the paginated list of calls for the tenant authenticated by the credential.

## Pagination

- `page` and `page_size` follow the same contract used by the public campaigns list.
- The response uses `count`, `next`, `previous`, and `results`.
- `page` is 1-based.
- `page_size` defaults to `25` and maxes out at `100`.

## Filters and ordering

- `status`: one or more call statuses.
- `direction`: one or more call directions.
- `origin`: one or more origins.
- `call_type`: one or more canonical call types.
- `campaign_id`: filter calls for a specific campaign.
- `campaign_contact_id`: filter calls for a specific campaign contact.
- `public_api_call_contact_id`: filter calls for a specific public API contact job.
- `public_api_call_contact_phone_id`: filter calls for one phone row within a public API contact job.
- `public_api_call_contact_status`: one or more parent public API contact job statuses.
- `public_api_call_contact_phone_status`: one or more phone statuses within a public API contact job.
- `outcome`: `converted`, `mismatch`, `optout`, or `ivr_heard_to_end`.
- `is_converted`, `is_optout`, `is_mismatch`: boolean filters.
- `has_transfer`, `has_human_answer`, `has_ai_answer`, `has_ai_refund`: boolean operational filters.
- `ivr_selected_digit`: one or more selected DTMF digits.
- `participant_user`: one or more user IDs involved in human handling.
- `region`, `state_code`, `ddd`, `phone_type`: destination-number filters.
- `start_date`, `end_date`: activity period filters.
- `ordering`: accepts the documented public ordering fields.
- `search`: text search across numbers, agent, campaign, contact, and related public IDs.

<ApiCodeExamples exampleId="callsList" />

<ApiResponseExamples exampleId="callsList" />

## Main fields per item

- `id`: call ID.
- `canonical_url`: canonical Contact-Tel app link for the call.
- `status`, `direction`, `origin`, `call_type`: operational classification.
- `origin` may include `campaign`, `manual`, or `public_api`.
- `to_number`, `from_number`: phone numbers involved in the call.
- `public_api_call_contact_id`, `public_api_call_contact_status`: parent public API contact job correlation.
- `client_reference`: opaque client reference from the parent job, or `null` for calls not created through the public API.
- `retry_policy`: immutable parent-job retry policy snapshot, or `null` for legacy/non-public calls.
- `attempts`: parent-job dial progress with `count`, `remaining`, and `next_attempt_at`.
- `public_api_call_contact_phone_id`, `public_api_call_contact_phone_status`, `public_api_call_contact_phone_position`: phone-attempt correlation inside the public API contact job.
- `duration`: operational call duration.
- `ai_billed_seconds`, `ai_gross_billed_seconds`, `ai_refunded_seconds`: net, gross and refunded AI consumption.
- `telephony_billed_seconds`, `telephony_gross_billed_seconds`, `telephony_refunded_seconds`: net, gross and refunded telephony consumption.
- `is_converted`, `is_optout`, `is_mismatch`: key outcomes exposed for integrations.
- `created_by_id`, `created_by_name`, `created_by_extension`: creator/originator user reference.
- `transfer_target_user_*`, `transfer_target_group_*`, `interaction_claimed_by_*`, `interaction_answered_by_*`: human transfer and participant references.
- `telephony_transfer_*`: human handoff timeline.

## Option reference

- `direction`: `outbound` means the platform placed the call; `inbound` means the platform received it.
- `origin`: `campaign` is campaign-created; `manual` is user/operator-created; `public_api` is created by public API integration.
- `call_type`: `ai_agent` is an AI-agent call; `reverse_ivr` is audio plus DTMF; `sip` is a SIP call.
- `outcome`: semantic outcome filter; `ivr_heard_to_end` applies to Reverse IVR playback.
- `to_number_phone_type`: `unknown`, `mobile`, `landline`, or `sip`.
- `status`: `pending`, `queued`, `dialing`, `analyzing`, `ringing`, `in_progress`, `finalizing`, `waiting_capacity`, `waiting_operating_hours`, `completed`, `no_interaction`, `failed`, `no_answer`, `no_answer_timeout`, `busy`, `cancelled`, `transfer_unanswered`, `congestion`, `voicemail`, `invalid_number`, `config_error`, `declined`, `unreachable`, `timeout`, `mismatch`, `api_timeout`, `terminated_manual`, `capacity_timeout`, `operating_window_timeout`.

## When to use this endpoint

- track call history per campaign;
- feed external monitoring;
- sync operational call outcomes into client systems.
